/**
 * SoftwareIQ Module Dashboard Page
 *
 * WHY: This is the landing page for the SoftwareIQ module.
 * It provides module-specific metrics and quick access to:
 * - Software Assurance workflows (Findings, Vetting, Schemas)
 * - Vulnerability Assessment (future)
 * - Mission context within the module
 * - Module-specific KPIs and trends
 */

import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { useMissionContext } from '@/hooks/useMissionContext'
import { api, type Observation } from '@/api'
import { MissionStatusCard, RiskTrendChart, ActivityFeed, ScannerStatusWidget } from '@/components/dashboard'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Target,
  ArrowRight,
  Shield,
  ClipboardCheck,
  FileCode,
  ShieldCheck,
  Bug,
  AlertTriangle,
  Clock,
} from 'lucide-react'

// Workflow cards for SoftwareIQ
const workflowCards = [
  {
    id: 'software-assurance',
    name: 'Software Assurance',
    description: 'JFAC vetting workflow for software findings',
    icon: ShieldCheck,
    href: '/softwareiq/assurance/findings',
    available: true,
    items: [
      { name: 'Findings', href: '/softwareiq/assurance/findings', icon: Shield },
      { name: 'Vetting Queue', href: '/softwareiq/assurance/vetting', icon: ClipboardCheck },
      { name: 'Schemas', href: '/softwareiq/assurance/schemas', icon: FileCode },
    ],
  },
  {
    id: 'vulnerability-assessment',
    name: 'Vulnerability Assessment',
    description: 'Security vulnerability scanning & remediation',
    icon: Bug,
    href: '#',
    available: false,
    items: [],
  },
]

export function SoftwareIQDashboardPage() {
  useAuth() // Ensure authenticated
  const { selectedMissionId, setSelectedMissionId } = useMissionContext()

  // Fetch missions
  const { data: missions = [] } = useQuery({
    queryKey: ['platform', 'missions'],
    queryFn: () => api.platform.missions.list(),
  })

  // Fetch pending observations
  const { data: pendingObservations = [] } = useQuery<Observation[]>({
    queryKey: ['softwareiq', 'observations', 'pending'],
    queryFn: () => api.softwareiq.observations.list({ status: 'pending' }),
  })

  const stats = {
    totalMissions: missions.length,
    activeMissions: missions.filter(m => m.status === 'active').length,
    pendingVetting: pendingObservations.length,
    criticalFindings: pendingObservations.filter(o => o.severity === 'Critical').length,
    highFindings: pendingObservations.filter(o => o.severity === 'High').length,
  }

  const selectedMission = missions.find(m => m.mission_id === selectedMissionId)

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Module Header */}
      <div className="p-2">
        <div className="flex items-start justify-between gap-6">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 rounded-lg bg-blue-500/20">
                <ShieldCheck className="h-6 w-6 text-blue-400" />
              </div>
            </div>
            <h1 className="text-2xl font-display font-bold">
              Software Assurance Dashboard
            </h1>
            <p className="mt-2 text-muted-foreground max-w-xl">
              {stats.pendingVetting} observations awaiting vetting •{' '}
              {stats.criticalFindings} critical • {stats.highFindings} high severity
            </p>
            <div className="mt-6 flex gap-4">
              <Button asChild variant="glow">
                <Link to="/softwareiq/assurance/vetting">
                  Review Vetting Queue
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button asChild variant="outline">
                <Link to="/softwareiq/assurance/findings">
                  View All Findings
                </Link>
              </Button>
            </div>
          </div>

          {/* Mission Context Selector */}
          {missions.length > 0 && (
            <div className="min-w-[250px]">
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2 block">
                Mission Context
              </label>
              <Select
                value={selectedMissionId || undefined}
                onValueChange={setSelectedMissionId}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a mission" />
                </SelectTrigger>
                <SelectContent>
                  {missions.map((mission) => (
                    <SelectItem key={mission.mission_id} value={mission.mission_id}>
                      <div className="flex items-center gap-2">
                        <Target className="h-4 w-4" />
                        <span>{mission.mission_name}</span>
                        {mission.status === 'active' && (
                          <Badge variant="default" className="text-xs ml-2">
                            Active
                          </Badge>
                        )}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Active Missions
            </CardTitle>
            <Target className="h-4 w-4 text-primary" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{stats.activeMissions}</div>
            <p className="text-xs text-muted-foreground mt-1">
              of {stats.totalMissions} total
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Pending Vetting
            </CardTitle>
            <Clock className="h-4 w-4 text-yellow-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{stats.pendingVetting}</div>
            <p className="text-xs text-muted-foreground mt-1">
              awaiting SME review
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Critical Findings
            </CardTitle>
            <AlertTriangle className="h-4 w-4 text-red-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-red-400">{stats.criticalFindings}</div>
            <p className="text-xs text-muted-foreground mt-1">
              require immediate action
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Workflow Cards */}
      <div>
        <h2 className="text-lg font-semibold mb-4">Workflows</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {workflowCards.filter(w => w.available).map((workflow) => {
            const WorkflowIcon = workflow.icon

            return (
              <Card
                key={workflow.id}
                className="transition-all duration-300 hover:border-primary/50"
              >
                <CardHeader>
                  <div className="p-2 rounded-lg bg-background/50 w-fit">
                    <WorkflowIcon className="h-5 w-5" />
                  </div>
                  <CardTitle className="mt-3 text-base">{workflow.name}</CardTitle>
                  <CardDescription className="text-sm">{workflow.description}</CardDescription>
                </CardHeader>
                {workflow.items.length > 0 && (
                  <CardContent className="pt-0">
                    <div className="space-y-1">
                      {workflow.items.map((item) => {
                        const ItemIcon = item.icon
                        return (
                          <Button
                            key={item.href}
                            asChild
                            variant="ghost"
                            className="w-full justify-start h-9"
                          >
                            <Link to={item.href}>
                              <ItemIcon className="h-4 w-4 mr-2" />
                              {item.name}
                            </Link>
                          </Button>
                        )
                      })}
                    </div>
                  </CardContent>
                )}
              </Card>
            )
          })}
        </div>
      </div>

      {/* Mission-Specific Widgets */}
      {selectedMissionId && selectedMission && (
        <>
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-primary" />
              <h3 className="text-lg font-semibold">
                Mission: {selectedMission.mission_name}
              </h3>
              <Badge variant="outline">{selectedMission.status}</Badge>
            </div>

            <MissionStatusCard missionId={selectedMissionId} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <RiskTrendChart missionId={selectedMissionId} className="lg:col-span-2" />
            <ActivityFeed className="lg:col-span-1" limit={8} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <ScannerStatusWidget />
          </div>
        </>
      )}

      {/* Empty State */}
      {!selectedMissionId && missions.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center">
            <Target className="h-16 w-16 text-muted-foreground mx-auto mb-4 opacity-50" />
            <h3 className="text-lg font-medium">No Missions Yet</h3>
            <p className="text-muted-foreground mt-2 max-w-md mx-auto">
              Create a mission to start tracking software assurance findings
              and begin the JFAC vetting workflow.
            </p>
            <Button asChild className="mt-6">
              <Link to="/missions">
                Create Mission
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
